/**
 * Command and procedure contracts (architecture §6.1, §6.3). Contracts are values: call sites import the contract
 * object (no global route-type union). The server pairs each with exactly one implementation carrying an `authorize`
 * policy rule (startup refuses a contract without one, default deny); `pnpm gen` collects implementations.
 */
import type { z } from 'zod';
import type { Op } from './sync/protocol';

/** Read access to the client store for `predict` (the optimistic overlay of a command). */
export interface PredictStore {
  get(model: string, id: string): Readonly<Record<string, unknown>> | undefined;
}

export type PredictedOp = Exclude<Op, { o: 'command' }>;

export interface CommandContract<N extends string = string, A = unknown> {
  readonly kind: 'command';
  readonly name: N;
  readonly args: z.ZodType<A>;
  /** arg path → model: resolved under RLS before `authorize` (a missing or invisible id ⇒ `not_found` with `field`). */
  readonly refs: Readonly<Record<string, string>>;
  /** Optimistic overlay; the client never runs server logic. */
  readonly predict?: (store: PredictStore, args: A) => PredictedOp[];
  /** Effects beyond one bounded transaction continue in `command.bulk` and complete through `CommandJob`. */
  readonly jobBacked?: boolean;
}

export function defineCommand<const N extends string, A>(c: Omit<CommandContract<N, A>, 'kind'>): CommandContract<N, A> {
  return Object.freeze({ kind: 'command' as const, ...c });
}

export interface RateLimit {
  /** tokens per second (or per `per`) */
  readonly limit: number;
  readonly burst?: number;
  readonly per?: 'second' | 'minute' | 'hour' | 'day';
  readonly key?: 'session' | 'user' | 'ip';
}

export interface ProcedureContract<N extends string = string, I = unknown, O = unknown> {
  readonly name: N;
  /** `query` runs in `withRead`; `command` in `withWrite`/`withRead` plus side effects. */
  readonly kind: 'query' | 'command';
  /** `workspace`: `POST /api/w/:ws/rpc/<name>`; `account`: `POST /api/rpc/<name>`. */
  readonly scope: 'workspace' | 'account';
  readonly input: z.ZodType<I>;
  /** Parsed in dev and test (catches an accidental email in a guest-visible payload), skipped in production. */
  readonly output: z.ZodType<O>;
  readonly rateLimit?: RateLimit;
  /** Side-effecting procedures require an `Idempotency-Key` header (§6.5). */
  readonly idempotent?: boolean;
}

export function defineProcedure<const N extends string, I, O>(
  p: Omit<ProcedureContract<N, I, O>, 'scope'> & { scope?: 'workspace' | 'account' },
): ProcedureContract<N, I, O> {
  return Object.freeze({ ...p, scope: p.scope ?? 'workspace' });
}

/** Default procedure rate limit (§6.6): 20/s, burst 40 per session. */
export const DEFAULT_RPC_LIMIT: RateLimit = { limit: 20, burst: 40, per: 'second', key: 'session' };
